<template>
  <v-col cols="12">
    <v-card :loading="loading" outlined class="faixa-etaria-card mx-auto mt-2">
      <template slot="progress">
        <v-progress-linear color="primary" height="5" indeterminate></v-progress-linear>
      </template>

      <div
        class="faixa-etaria-card__area"
        :class="{ 'faixa-etaria-card__area--rolavel': rolagemHorizontal }"
      >
        <div :style="{ minWidth: larguraMinimaDoGrafico }">
          <apexchart
            type="line"
            :height="alturaDoGrafico"
            :options="options"
            :series="series"
          ></apexchart>
        </div>
      </div>
    </v-card>
  </v-col>
</template>

<script>
export default {
  name: "FaixaEtariaCard",

  props: {
    franquiaId: {
      type: Number,
      default: null,
    },
  },

  watch: {
    franquiaId: {
      handler(value) {
        this.getFaixaEtaria(value);
      },
      immediate: true,
    },
  },

  data() {
    return {
      loading: true,
      options: this.montarOptions([], 1),
      quantidadeDeIdades: 0,
      series: this.montarSeries([]),
    };
  },

  computed: {
    alturaDoGrafico() {
      return this.$vuetify.breakpoint.xsOnly ? 340 : 400;
    },

    rolagemHorizontal() {
      return this.$vuetify.breakpoint.smAndDown && this.quantidadeDeIdades > 6;
    },

    larguraMinimaDoGrafico() {
      return this.rolagemHorizontal ? `${this.quantidadeDeIdades * 46}px` : "auto";
    },
  },

  methods: {
    larguraDaColuna(quantidadeDeIdades) {
      if (quantidadeDeIdades <= 3) return "20%";
      if (quantidadeDeIdades <= 6) return "40%";
      if (quantidadeDeIdades <= 12) return "60%";
      return "80%";
    },

    montarSeries(quantidades) {
      return [
        {
          data: quantidades,
          name: "Alunos",
          type: "column",
        },
        {
          data: quantidades,
          name: "Evolução",
          type: "line",
        },
      ];
    },

    montarOptions(categorias, max) {
      return {
        chart: {
          foreColor: "#263238",
          height: 350,
          toolbar: {
            show: true,
          },
          type: "line",
          zoom: {
            enabled: false,
          },
        },

        colors: ["#0d6efd", "#212529"],

        dataLabels: {
          enabled: true,
          enabledOnSeries: [0],
          offsetY: -22,
          style: {
            colors: ["#263238"],
            fontSize: "11px",
            fontWeight: "bold",
          },
        },

        legend: {
          show: false,
        },

        markers: {
          colors: ["#ffffff", "#ffffff"],
          size: [0, 5],
          strokeColors: "#0d6efd",
          strokeWidth: 2,
        },

        plotOptions: {
          bar: {
            columnWidth: this.larguraDaColuna(categorias.length),
            dataLabels: {
              position: "top",
            },
            endingShape: "rounded",
          },
        },

        responsive: [
          {
            breakpoint: 960,
            options: {
              dataLabels: {
                offsetY: -18,
                style: {
                  fontSize: "10px",
                },
              },
              plotOptions: {
                bar: {
                  columnWidth: "70%",
                },
              },
              title: {
                style: {
                  fontSize: "14px",
                },
              },
              xaxis: {
                labels: {
                  rotate: -45,
                  rotateAlways: true,
                  style: {
                    fontSize: "10px",
                  },
                },
              },
            },
          },

          {
            breakpoint: 600,
            options: {
              chart: {
                toolbar: {
                  show: false,
                },
              },
              dataLabels: {
                offsetY: -14,
                style: {
                  fontSize: "9px",
                },
              },
              markers: {
                size: [0, 4],
              },
              stroke: {
                width: [0, 3],
              },
              title: {
                style: {
                  fontSize: "13px",
                },
              },
              xaxis: {
                labels: {
                  style: {
                    fontSize: "9px",
                  },
                },
                title: {
                  text: "",
                },
              },
            },
          },
        ],

        stroke: {
          width: [0, 4],
        },

        title: {
          style: {
            color: "#263238",
            fontFamily: undefined,
            fontSize: "16px",
            fontWeight: "bold",
          },
          text: "Gráfico de faixa etária dos alunos",
        },

        tooltip: {
          enabledOnSeries: [0],
          intersect: false,
          shared: true,
        },

        xaxis: {
          categories: categorias,
          labels: {
            hideOverlappingLabels: false,
            rotate: -45,
            rotateAlways: categorias.length > 8,
            style: {
              fontSize: "12px",
            },
          },
          tickPlacement: "on",
          title: {
            text: "Idade",
          },
        },

        yaxis: [
          {
            forceNiceScale: true,
            labels: {
              formatter: (valor) => Math.round(valor),
            },
            max: Math.ceil(max * 1.2),
            min: 0,
            title: {
              text: "Alunos",
            },
          },
          {
            max: Math.ceil(max * 1.2),
            min: 0,
            show: false,
          },
        ],
      };
    },

    async getFaixaEtaria(franquiaId) {
      this.loading = true;

      try {
        const response = await this.$services.alunosService.faixaEtaria(franquiaId);
        const faixas = response.data || [];

        const categorias = faixas.map((faixa) => `${faixa.idade} anos`);
        const quantidades = faixas.map((faixa) => parseInt(faixa.quantidade, 10));

        this.quantidadeDeIdades = faixas.length;
        this.options = this.montarOptions(categorias, Math.max(...quantidades, 1));
        this.series = this.montarSeries(quantidades);
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.faixa-etaria-card__area {
  width: 100%;
}

.faixa-etaria-card__area--rolavel {
  -webkit-overflow-scrolling: touch;
  overflow-x: auto;
}
</style>
